import React from 'react';
import { Statistic } from '../components/StockTable';
import { GoldenCrossIcon, DeathCrossIcon, SupportIcon, ResistanceIcon } from '../components/Icons';

// Technical statistics configuration organized by category
export const TECHNICAL_STATISTICS: Statistic[] = [
  // Range Analysis
  { key: '52W_HIGH', label: '52-Week High' },
  { key: 'last_closing_price', label: 'Last Closing Price' },
  { key: '52W_LOW', label: '52-Week Low' },
  { key: 'pct_off_52w_high', label: '% Below 52-Week High' },
  { key: 'pct_above_52w_low', label: '% Above 52-Week Low' },
  
  // Simple Moving Average
  { key: 'SMA_5', label: '5-Day SMA' },
  { key: 'SMA_10', label: '10-Day SMA' },
  { key: 'SMA_20', label: '20-Day SMA' },
  { key: 'SMA_50', label: '50-Day SMA' },
  { key: 'SMA_100', label: '100-Day SMA' },
  { key: 'SMA_200', label: '200-Day SMA' },
  { key: 'SMA_W50', label: '50-Week SMA' },
  { key: 'SMA_W200', label: '200-Week SMA' },
  
  // Volume Moving Average
  { key: 'VMA_5', label: '5-Day VMA' },
  { key: 'VMA_10', label: '10-Day VMA' },
  { key: 'VMA_20', label: '20-Day VMA' },
  { key: 'VMA_50', label: '50-Day VMA' },
  { key: 'VMA_100', label: '100-Day VMA' },
  { key: 'VMA_200', label: '200-Day VMA' },
  
  // Exponential Moving Average
  { key: 'EMA_9', label: '9-Day EMA' },
  { key: 'EMA_12', label: '12-Day EMA' },
  { key: 'EMA_21', label: '21-Day EMA' },
  { key: 'EMA_26', label: '26-Day EMA' },
  { key: 'EMA_50', label: '50-Day EMA' },
  { key: 'EMA_200', label: '200-Day EMA' },
  
  // Relative Strength Index
  { key: 'RSI_9', label: '9-Day RSI' },
  { key: 'RSI_14', label: '14-Day RSI' },
  { key: 'RSI_21', label: '21-Day RSI' },
  
  // Moving Average Convergence Divergence (MACD)
  { key: 'MACD_LINE', label: 'MACD Line' },
  { key: 'MACD_SIGNAL', label: 'MACD Signal' },
  { key: 'MACD_HIST', label: 'MACD Histogram' },
  
  // Stochastic Oscillator
  { key: 'STOCH_9_K', label: '9-Day %K' },
  { key: 'STOCH_9_D', label: '9-Day %D' },
  { key: 'STOCH_14_K', label: '14-Day %K' },
  { key: 'STOCH_14_D', label: '14-Day %D' },
  { key: 'STOCH_21_K', label: '21-Day %K' },
  { key: 'STOCH_21_D', label: '21-Day %D' },
  
  // Bollinger Bands
  { key: 'BB_20_UPPER_20', label: '20-Day Upper BB' },
  { key: 'BB_20_SMA', label: '20-Day Middle BB' },
  { key: 'BB_20_LOWER_20', label: '20-Day Lower BB' },
  
  // Fibonacci Levels
  { key: 'FIB_SWING_PERIOD', label: 'Last 10%+ Swing Lasting 10+ Days' },
  { key: 'FIB_SWING_PERCENTAGE', label: 'Swing %' },
  { key: 'FIB_0_0', label: '0% Retracement' },
  { key: 'FIB_0_236', label: '23.6% Retracement' },
  { key: 'FIB_0_382', label: '38.2% Retracement' },
  { key: 'FIB_0_5', label: '50% Retracement' },
  { key: 'FIB_0_618', label: '61.8% Retracement' },
  { key: 'FIB_0_786', label: '78.6% Retracement' },
  { key: 'FIB_1_0', label: '100% Retracement' },
];

// Category headers for organizing statistics
export const CATEGORY_HEADERS = [
  { startIndex: 0, label: 'Range Analysis' },
  { startIndex: 5, label: 'Simple Moving Average' },
  { startIndex: 13, label: 'Volume Moving Average' },
  { startIndex: 19, label: 'Exponential Moving Average' },
  { startIndex: 25, label: 'Relative Strength Index' },
  { startIndex: 28, label: 'MACD' },
  { startIndex: 31, label: 'Stochastic Oscillator' },
  { startIndex: 37, label: 'Bollinger Bands' },
  { startIndex: 40, label: 'Fibonacci Levels' },
];

// Technical data interfaces
export interface TechnicalData {
  ticker_basics_id: number;
  indicator_key: string;
  value: number | null;
  nonNumericValue?: string | null;
  date: string;
}

export interface PriceData {
  ticker_symbol: string;
  ticker_basics_id: number;
  close_price_adjusted: number;
  date: Date;
  previous_close_price_adjusted?: number | null;
  previous_date?: Date | null;
}

export interface MovingAverageData {
  days_ago: number;
  date: Date;
  sma_20?: number | null;
  sma_50?: number | null;
  sma_200?: number | null;
  sma_w50?: number | null;
  sma_w200?: number | null;
  ema_21?: number | null;
  ema_50?: number | null;
  ema_200?: number | null;
}

export interface TickerTechnicalData {
  [tickerSymbol: string]: {
    technicals: { [key: string]: TechnicalData };
    price: PriceData | null;
    movingAverages: MovingAverageData[];
  };
}

// Format number with commas
export const formatNumber = (num: number, decimals: number = 2): string => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Function to detect golden cross for a given indicator pair
export const hasGoldenCross = (
  tickerData: TickerTechnicalData[string],
  currentIndicator: string,
  compareIndicator: string
): boolean => {
  if (!tickerData || !tickerData.movingAverages || tickerData.movingAverages.length === 0) {
    return false;
  }

  // Get current values (days_ago = 0) from technicals
  const currentValue = tickerData.technicals[currentIndicator]?.value;
  const currentCompareValue = tickerData.technicals[compareIndicator]?.value;

  if (currentValue === undefined || currentValue === null || currentCompareValue === undefined || currentCompareValue === null) {
    return false;
  }

  // Check if current value > compare value (golden cross condition)
  if (currentValue <= currentCompareValue) {
    return false;
  }

  // Check if there was a cross in the historical data (days_ago 1-6)
  return tickerData.movingAverages.some(ma => {
    const historicalValue = ma[currentIndicator.toLowerCase() as keyof MovingAverageData];
    const historicalCompareValue = ma[compareIndicator.toLowerCase() as keyof MovingAverageData];
    
    if (historicalValue === null || historicalValue === undefined || 
        historicalCompareValue === null || historicalCompareValue === undefined) {
      return false;
    }

    // Check if historical value was <= historical compare value (indicating a cross occurred)
    return historicalValue <= historicalCompareValue;
  });
};

// Function to detect death cross for a given indicator pair
export const hasDeathCross = (
  tickerData: TickerTechnicalData[string],
  currentIndicator: string,
  compareIndicator: string
): boolean => {
  if (!tickerData || !tickerData.movingAverages || tickerData.movingAverages.length === 0) {
    return false;
  }

  // Get current values (days_ago = 0) from technicals
  const currentValue = tickerData.technicals[currentIndicator]?.value;
  const currentCompareValue = tickerData.technicals[compareIndicator]?.value;

  if (currentValue === undefined || currentValue === null || currentCompareValue === undefined || currentCompareValue === null) {
    return false;
  }

  // Check if current value < compare value (death cross condition - reversed from golden cross)
  if (currentValue >= currentCompareValue) {
    return false;
  }

  // Check if there was a cross in the historical data (days_ago 1-6)
  return tickerData.movingAverages.some(ma => {
    const historicalValue = ma[currentIndicator.toLowerCase() as keyof MovingAverageData];
    const historicalCompareValue = ma[compareIndicator.toLowerCase() as keyof MovingAverageData];
    
    if (historicalValue === null || historicalValue === undefined || 
        historicalCompareValue === null || historicalCompareValue === undefined) {
      return false;
    }

    // Check if historical value was >= historical compare value (indicating a cross occurred from above to below)
    return historicalValue >= historicalCompareValue;
  });
};

// Helper function to detect if price crossed above a Bollinger Band within past 6 days
export const hasBBGoldenCross = (tickerData: TickerTechnicalData[string], bbKey: string): boolean => {
  if (!tickerData || !tickerData.price) return false;
  
  const currentPrice = tickerData.price.close_price_adjusted;
  const previousPrice = tickerData.price.previous_close_price_adjusted;
  const bbValue = tickerData.technicals[bbKey]?.value;
  
  if (currentPrice === undefined || currentPrice === null || 
      bbValue === undefined || bbValue === null) {
    return false;
  }
  
  // Current price must be above the BB
  if (currentPrice <= bbValue) {
    return false;
  }
  
  // Check if price just crossed above (previous price was below or equal)
  if (previousPrice !== undefined && previousPrice !== null) {
    return previousPrice <= bbValue;
  }
  
  // If no previous price, we can't determine if it just crossed
  return false;
};

// Helper function to detect if price crossed below a Bollinger Band within past 6 days
export const hasBBDeathCross = (tickerData: TickerTechnicalData[string], bbKey: string): boolean => {
  if (!tickerData || !tickerData.price) return false;
  
  const currentPrice = tickerData.price.close_price_adjusted;
  const previousPrice = tickerData.price.previous_close_price_adjusted;
  const bbValue = tickerData.technicals[bbKey]?.value;
  
  if (currentPrice === undefined || currentPrice === null || 
      bbValue === undefined || bbValue === null) {
    return false;
  }
  
  // Current price must be below the BB
  if (currentPrice >= bbValue) {
    return false;
  }
  
  // Check if price just crossed below (previous price was above or equal)
  if (previousPrice !== undefined && previousPrice !== null) {
    return previousPrice >= bbValue;
  }
  
  // If no previous price, we can't determine if it just crossed
  return false;
};

// Helper function to get Bollinger Band icons (golden cross, death cross, support, resistance)
export const getBollingerBandIcon = (tickerData: TickerTechnicalData[string], statisticKey: string): React.ReactNode => {
  if (!tickerData || !tickerData.price) return null;
  
  const currentPrice = tickerData.price.close_price_adjusted;
  const bbValue = tickerData.technicals[statisticKey]?.value;
  
  if (currentPrice === undefined || currentPrice === null || 
      bbValue === undefined || bbValue === null) {
    return null;
  }
  
  // Get all BB values
  const upperBB = tickerData.technicals['BB_20_UPPER_20']?.value;
  const middleBB = tickerData.technicals['BB_20_SMA']?.value;
  const lowerBB = tickerData.technicals['BB_20_LOWER_20']?.value;
  
  if (upperBB === undefined || upperBB === null ||
      middleBB === undefined || middleBB === null ||
      lowerBB === undefined || lowerBB === null) {
    return null;
  }
  
  // Check for golden cross (price crossed above this band)
  if (hasBBGoldenCross(tickerData, statisticKey)) {
    const bandName = statisticKey.includes('UPPER') ? 'Upper' : 
                     statisticKey.includes('SMA') ? 'Middle' : 'Lower';
    return (
      <span className="ml-1 group/golden-cross relative">
        <GoldenCrossIcon className="w-4 h-4" />
        <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
          Price crossed above {bandName} Bollinger Band within the past 6 days
        </div>
      </span>
    );
  }
  
  // Check for death cross (price crossed below this band)
  if (hasBBDeathCross(tickerData, statisticKey)) {
    const bandName = statisticKey.includes('UPPER') ? 'Upper' : 
                     statisticKey.includes('SMA') ? 'Middle' : 'Lower';
    return (
      <span className="ml-1 group/death-cross relative">
        <DeathCrossIcon className="w-4 h-4" />
        <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
          Price crossed below {bandName} Bollinger Band within the past 6 days
        </div>
      </span>
    );
  }
  
  // Determine the closest lower and higher bands
  let closestLowerBand: string | null = null;
  let closestHigherBand: string | null = null;
  
  // Find closest lower band (largest band value that is still < current price)
  if (currentPrice > lowerBB) {
    if (currentPrice >= middleBB) {
      if (currentPrice >= upperBB) {
        closestLowerBand = 'BB_20_UPPER_20';
      } else {
        closestLowerBand = 'BB_20_SMA';
      }
    } else {
      closestLowerBand = 'BB_20_LOWER_20';
    }
  }
  
  // Find closest higher band (smallest band value that is still > current price)
  if (currentPrice < upperBB) {
    if (currentPrice < middleBB) {
      if (currentPrice < lowerBB) {
        closestHigherBand = 'BB_20_LOWER_20';
      } else {
        closestHigherBand = 'BB_20_SMA';
      }
    } else {
      closestHigherBand = 'BB_20_UPPER_20';
    }
  }
  
  // Show support icon only on the closest lower band
  if (closestLowerBand === statisticKey) {
    const bandName = statisticKey.includes('UPPER') ? 'Upper' : 
                     statisticKey.includes('SMA') ? 'Middle' : 'Lower';
    return (
      <span className="ml-1 group/support relative">
        <SupportIcon className="w-4 h-4" />
        <div className="absolute hidden group-hover/support:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
          Price is above {bandName} Bollinger Band (support level)
        </div>
      </span>
    );
  }
  
  // Show resistance icon only on the closest higher band
  if (closestHigherBand === statisticKey) {
    const bandName = statisticKey.includes('UPPER') ? 'Upper' : 
                     statisticKey.includes('SMA') ? 'Middle' : 'Lower';
    return (
      <span className="ml-1 group/resistance relative">
        <ResistanceIcon className="w-4 h-4" />
        <div className="absolute hidden group-hover/resistance:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
          Price is below {bandName} Bollinger Band (resistance level)
        </div>
      </span>
    );
  }
  
  return null;
};

// Helper function to determine which Fibonacci icon to show for a given level
export const getFibonacciIcon = (tickerData: TickerTechnicalData[string], statisticKey: string): React.ReactNode => {
  if (!tickerData) return null;

  // Get swing direction
  const swingDirection = tickerData.technicals['FIB_SWING_DIRECTION']?.nonNumericValue;
  if (!swingDirection || (swingDirection !== 'up' && swingDirection !== 'down')) {
    return null;
  }

  // Get last closing price
  const lastClosingPrice = tickerData.price?.close_price_adjusted;
  if (lastClosingPrice === undefined || lastClosingPrice === null) {
    return null;
  }

  // Get current Fibonacci level value
  const currentLevelValue = tickerData.technicals[statisticKey]?.value;
  if (currentLevelValue === undefined || currentLevelValue === null) {
    return null;
  }

  // Get all Fibonacci retracement levels
  const fibLevels = ['FIB_0_0', 'FIB_0_236', 'FIB_0_382', 'FIB_0_5', 'FIB_0_618', 'FIB_0_786', 'FIB_1_0'];
  const fibValues: { [key: string]: number } = {};
  
  for (const level of fibLevels) {
    const value = tickerData.technicals[level]?.value;
    if (value !== undefined && value !== null) {
      fibValues[level] = value;
    }
  }

  if (swingDirection === 'up') {
    // For up swing:
    // - Death cross icon: retracement level with lowest value that is higher than last closing price
    // - Support icon: retracement level with highest value that is lower than last closing price
    
    // Find levels higher than closing price
    const levelsAbove = Object.entries(fibValues)
      .filter(([_, value]) => value > lastClosingPrice)
      .sort(([_, a], [__, b]) => a - b); // Sort by value ascending
    
    // Find levels lower than closing price
    const levelsBelow = Object.entries(fibValues)
      .filter(([_, value]) => value < lastClosingPrice)
      .sort(([_, a], [__, b]) => b - a); // Sort by value descending

    // Show death cross icon if this is the lowest level above closing price
    if (levelsAbove.length > 0 && levelsAbove[0][0] === statisticKey) {
      return (
        <span className="ml-1 group/death-cross relative">
          <DeathCrossIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Last closing price broke below this support level
          </div>
        </span>
      );
    }

    // Show support icon if this is the highest level below closing price
    if (levelsBelow.length > 0 && levelsBelow[0][0] === statisticKey) {
      return (
        <span className="ml-1 group/support relative">
          <SupportIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/support:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Next support level
          </div>
        </span>
      );
    }
  } else {
    // For down swing:
    // - Golden cross icon: retracement level with highest value that is lower than last closing price
    // - Resistance icon: retracement level with lowest value that is higher than last closing price
    
    // Find levels lower than closing price
    const levelsBelow = Object.entries(fibValues)
      .filter(([_, value]) => value < lastClosingPrice)
      .sort(([_, a], [__, b]) => b - a); // Sort by value descending
    
    // Find levels higher than closing price
    const levelsAbove = Object.entries(fibValues)
      .filter(([_, value]) => value > lastClosingPrice)
      .sort(([_, a], [__, b]) => a - b); // Sort by value ascending

    // Show golden cross icon if this is the highest level below closing price
    if (levelsBelow.length > 0 && levelsBelow[0][0] === statisticKey) {
      return (
        <span className="ml-1 group/golden-cross relative">
          <GoldenCrossIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Last closing price broke above this resistance level
          </div>
        </span>
      );
    }

    // Show resistance icon if this is the lowest level above closing price
    if (levelsAbove.length > 0 && levelsAbove[0][0] === statisticKey) {
      return (
        <span className="ml-1 group/resistance relative">
          <ResistanceIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/resistance:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Next resistance level
          </div>
        </span>
      );
    }
  }
  
  return null;
};

// Helper function to get value color for technical statistics
export const getValueColor = (tickerData: TickerTechnicalData[string] | null | undefined, statisticKey: string): string => {
  if (!tickerData) return 'text-text-light-neutral dark:text-text-secondary';
  
  const high52w = tickerData.technicals['52W_HIGH']?.value;
  const low52w = tickerData.technicals['52W_LOW']?.value;
  const currentPrice = tickerData.price?.close_price_adjusted;
  
  if (!high52w || !low52w || !currentPrice || high52w <= 0 || low52w <= 0) {
    return 'text-text-light-neutral dark:text-text-secondary';
  }
  
  const pctOffHigh = ((high52w - currentPrice) / high52w) * 100;
  const pctAboveLow = ((currentPrice - low52w) / low52w) * 100;
  
  // For Last Closing Price: Compare with previous closing price
  if (statisticKey === 'last_closing_price') {
    const currentPrice = tickerData.price?.close_price_adjusted;
    const previousPrice = tickerData.price?.previous_close_price_adjusted;
    
    if (currentPrice !== undefined && currentPrice !== null && 
        previousPrice !== undefined && previousPrice !== null) {
      if (currentPrice > previousPrice) {
        return 'text-green-light dark:text-green-500';
      } else if (currentPrice < previousPrice) {
        return 'text-red-light dark:text-red-500';
      } else {
        return 'text-text-light-neutral dark:text-text-secondary';
      }
    }
  }
  
  // For % Below 52-Week High: Green if close to high, red if close to low
  if (statisticKey === 'pct_off_52w_high') {
    // Green if within 10% of 52-week high
    if (pctOffHigh <= 10) {
      return 'text-green-light dark:text-green-500';
    }
    
    // Red if within 10% of 52-week low
    if (pctAboveLow <= 10) {
      return 'text-red-light dark:text-red-500';
    }
  }
  
  // For % Above 52-Week Low: Only red if 10% or less, otherwise secondary
  if (statisticKey === 'pct_above_52w_low') {
    if (pctAboveLow <= 10) {
      return 'text-red-light dark:text-red-500';
    }
  }
  
  // VMA color logic
  if (statisticKey.startsWith('VMA_')) {
    const currentVMA = tickerData.technicals[statisticKey]?.value;
    
    if (currentVMA === undefined || currentVMA === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // Define VMA comparison pairs
    const vmaPairs: { [key: string]: string } = {
      'VMA_5': 'VMA_10',
      'VMA_10': 'VMA_20',
      'VMA_20': 'VMA_50',
      'VMA_50': 'VMA_100',
      'VMA_100': 'VMA_200'
    };
    
    const vmaCompareKey = vmaPairs[statisticKey];
    
    if (vmaCompareKey) {
      const compareVMA = tickerData.technicals[vmaCompareKey]?.value;
      
      if (compareVMA !== undefined && compareVMA !== null) {
        // Get corresponding SMA values for comparison
        const smaKey = statisticKey.replace('VMA_', 'SMA_');
        const smaCompareKey = vmaCompareKey.replace('VMA_', 'SMA_');
        
        const currentSMA = tickerData.technicals[smaKey]?.value;
        const compareSMA = tickerData.technicals[smaCompareKey]?.value;
        
        if (currentSMA !== undefined && currentSMA !== null && 
            compareSMA !== undefined && compareSMA !== null) {
          
          const vmaCondition = currentVMA >= compareVMA;
          const smaCondition = currentSMA >= compareSMA;
          
          // Green if both VMA and SMA conditions are true
          if (vmaCondition && smaCondition) {
            return 'text-green-light dark:text-green-500';
          }
          // Red if VMA condition is true but SMA condition is false
          else if (vmaCondition && !smaCondition) {
            return 'text-red-light dark:text-red-500';
          }
          // Secondary color for all other cases
          else {
            return 'text-text-light-neutral dark:text-text-secondary';
          }
        }
      }
    }
  }
  
  // EMA color logic
  if (statisticKey.startsWith('EMA_')) {
    const currentEMA = tickerData.technicals[statisticKey]?.value;
    
    if (currentEMA === undefined || currentEMA === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // 200-Day EMA is always secondary color (no longer period to compare to)
    if (statisticKey === 'EMA_200') {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // Define EMA comparison pairs
    const emaPairs: { [key: string]: string } = {
      'EMA_9': 'EMA_12',
      'EMA_12': 'EMA_21',
      'EMA_21': 'EMA_26',
      'EMA_26': 'EMA_50',
      'EMA_50': 'EMA_200'
    };
    
    const compareKey = emaPairs[statisticKey];
    
    if (compareKey) {
      const compareEMA = tickerData.technicals[compareKey]?.value;
      
      if (compareEMA !== undefined && compareEMA !== null) {
        return currentEMA >= compareEMA ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
      }
    }
  }
  
  // SMA color logic
  if (statisticKey.startsWith('SMA_')) {
    const currentSMA = tickerData.technicals[statisticKey]?.value;
    
    if (currentSMA === undefined || currentSMA === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // 200-Week SMA is always secondary color
    if (statisticKey === 'SMA_W200') {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // Define SMA comparison pairs
    const smaPairs: { [key: string]: string } = {
      'SMA_5': 'SMA_10',
      'SMA_10': 'SMA_20',
      'SMA_20': 'SMA_50',
      'SMA_50': 'SMA_100',
      'SMA_100': 'SMA_200',
      'SMA_200': 'SMA_W50',
      'SMA_W50': 'SMA_W200'
    };
    
    const compareKey = smaPairs[statisticKey];
    
    if (compareKey) {
      const compareSMA = tickerData.technicals[compareKey]?.value;
      
      if (compareSMA !== undefined && compareSMA !== null) {
        return currentSMA >= compareSMA ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
      }
    }
  }
  
  // RSI color logic
  if (statisticKey.startsWith('RSI_')) {
    const currentRSI = tickerData.technicals[statisticKey]?.value;
    
    if (currentRSI === undefined || currentRSI === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // Define RSI comparison pairs
    const rsiPairs: { [key: string]: string } = {
      'RSI_9': 'RSI_14',
      'RSI_14': 'RSI_21'
    };
    
    const compareKey = rsiPairs[statisticKey];
    
    if (compareKey) {
      const compareRSI = tickerData.technicals[compareKey]?.value;
      
      if (compareRSI !== undefined && compareRSI !== null) {
        return currentRSI >= compareRSI ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
      }
    }
  }
  
  // MACD color logic
  if (statisticKey === 'MACD_LINE' || statisticKey === 'MACD_SIGNAL' || statisticKey === 'MACD_HIST') {
    const macdValue = tickerData.technicals[statisticKey]?.value;
    
    if (macdValue === undefined || macdValue === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    if (macdValue > 0) {
      return 'text-green-light dark:text-green-500';
    } else if (macdValue < 0) {
      return 'text-red-light dark:text-red-500';
    } else {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
  }
  
  // Stochastic Oscillator color logic
  if (statisticKey.startsWith('STOCH_') && statisticKey.endsWith('_K')) {
    const currentK = tickerData.technicals[statisticKey]?.value;
    
    if (currentK === undefined || currentK === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // Get corresponding %D value
    const dKey = statisticKey.replace('_K', '_D');
    const currentD = tickerData.technicals[dKey]?.value;
    
    if (currentD !== undefined && currentD !== null) {
      if (currentK > currentD) {
        return 'text-green-light dark:text-green-500';
      } else if (currentK < currentD) {
        return 'text-red-light dark:text-red-500';
      } else {
        return 'text-text-light-neutral dark:text-text-secondary';
      }
    }
  }
  
  // Stochastic Oscillator %D color logic
  if (statisticKey.startsWith('STOCH_') && statisticKey.endsWith('_D')) {
    const currentD = tickerData.technicals[statisticKey]?.value;
    
    if (currentD === undefined || currentD === null) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // Define %D comparison pairs
    const dPairs: { [key: string]: string } = {
      'STOCH_9_D': 'STOCH_14_D',
      'STOCH_14_D': 'STOCH_21_D'
    };
    
    const compareKey = dPairs[statisticKey];
    
    if (compareKey) {
      const compareD = tickerData.technicals[compareKey]?.value;
      
      if (compareD !== undefined && compareD !== null) {
        if (currentD > compareD) {
          return 'text-green-light dark:text-green-500';
        } else if (currentD < compareD) {
          return 'text-red-light dark:text-red-500';
        } else {
          return 'text-text-light-neutral dark:text-text-secondary';
        }
      }
    }
  }
  
  // Bollinger Bands color logic
  if (statisticKey.startsWith('BB_')) {
    const currentPrice = tickerData.price?.close_price_adjusted;
    const upperBB = tickerData.technicals['BB_20_UPPER_20']?.value;
    const lowerBB = tickerData.technicals['BB_20_LOWER_20']?.value;
    
    if (currentPrice !== undefined && currentPrice !== null) {
      // Upper BB: green if current price > upper BB
      if (statisticKey === 'BB_20_UPPER_20' && upperBB !== undefined && upperBB !== null) {
        if (currentPrice > upperBB) {
          return 'text-green-light dark:text-green-500';
        }
      }
      
      // Lower BB: red if current price < lower BB
      if (statisticKey === 'BB_20_LOWER_20' && lowerBB !== undefined && lowerBB !== null) {
        if (currentPrice < lowerBB) {
          return 'text-red-light dark:text-red-500';
        }
      }
    }
  }
  
  // Fibonacci Swing Percentage color logic
  if (statisticKey === 'FIB_SWING_PERCENTAGE') {
    const swingPercentage = tickerData.technicals[statisticKey]?.value;
    if (swingPercentage !== undefined && swingPercentage !== null) {
      // Green for positive swing, red for negative swing
      return swingPercentage >= 0 ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500';
    }
  }
  
  // Default secondary color
  return 'text-text-light-neutral dark:text-text-secondary';
};

